/**
 * Compliance DSR console — submit + track Data Subject Requests.
 *
 * Two-tab UI:
 *   - GDPR  (default) — POSTs to /api/v1/gdpr-dsr
 *   - CCPA           — POSTs to /api/v1/ccpa-dsr
 * Both tabs share the same form fields (email / kind / region) and the same
 * "open requests" table underneath, so a privacy operator can work both
 * frameworks without leaving the page.
 *
 * Data layer: TanStack Query + Sonner. Form layer: local state + hand-rolled
 * safeParse in `./api.ts` (keeps this feature zero-dep on react-hook-form).
 * The file is intentionally large because root-owned brand rules forbid
 * adding shared primitives (Button, Select, Card) in this lane.
 */

import { useEffect, useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  complianceDsrQueryKeys,
  DEFAULT_REGION_FOR,
  DSR_FRAMEWORKS,
  DSR_KINDS,
  formatSlaCountdown,
  KIND_LABELS,
  listOpen,
  nextStatusesFor,
  REGIONS,
  slaTier,
  submitDsr,
  submitDsrSchema,
  updateStatus,
  type DsrFramework,
  type DsrKind,
  type DsrRegion,
  type DsrRow,
  type SlaTier,
  type UnifiedStatus,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — light theme, zinc borders, 6/8 rounding.
// ---------------------------------------------------------------------------

const CARD =
  "rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
  "w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";

const SLA_TIER_CLASS: Record<SlaTier, string> = {
  green: "border-emerald-200 bg-emerald-50 text-emerald-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  red: "border-red-200 bg-red-50 text-red-700",
};

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormState {
  email: string;
  kind: DsrKind;
  region: DsrRegion;
}

const EMPTY_FORM_FOR = (framework: DsrFramework): FormState => ({
  email: "",
  kind: "access",
  region: DEFAULT_REGION_FOR[framework],
});

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface ComplianceDsrPageProps {
  /** Optional initial framework — supports deep links / ?framework= search. */
  initialFramework?: DsrFramework;
  /** Injected clock for tests — defaults to Date.now. */
  now?: () => number;
}

export function ComplianceDsrPage(props: ComplianceDsrPageProps = {}) {
  const [framework, setFramework] = useState<DsrFramework>(
    props.initialFramework ?? "gdpr",
  );
  const nowFn = props.now ?? (() => Date.now());

  return (
    <main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-16">
      <Breadcrumb />
      <Heading />
      <FrameworkTabs active={framework} onChange={setFramework} />
      <SubmitCard framework={framework} />
      <OpenRequestsCard framework={framework} nowFn={nowFn} />
      <DocHint framework={framework} />
    </main>
  );
}

export default ComplianceDsrPage;

// ---------------------------------------------------------------------------
// Header fragments
// ---------------------------------------------------------------------------

function Breadcrumb() {
  return (
    <nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <a
            href="/"
            className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
          >
            Dashboard
          </a>
        </li>
        <li aria-hidden="true" className="text-zinc-300">
          /
        </li>
        <li>
          <span className="rounded-[6px] px-1 py-0.5">Compliance</span>
        </li>
        <li aria-hidden="true" className="text-zinc-300">
          /
        </li>
        <li className="text-zinc-900">Data subject requests</li>
      </ol>
    </nav>
  );
}

function Heading() {
  return (
    <header className="flex flex-col gap-2">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
        Compliance
      </p>
      <h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
        Data subject request console
      </h1>
      <p className="max-w-prose text-sm text-zinc-600">
        Submit and triage GDPR and CCPA requests in one place. Each request is
        logged, time-boxed against its statutory SLA, and routed to the correct
        residency region — EU for GDPR (30 days) and US-CA for CCPA (45 days).
      </p>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Framework tabs
// ---------------------------------------------------------------------------

function FrameworkTabs(props: {
  active: DsrFramework;
  onChange: (f: DsrFramework) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Regulatory framework"
      className="inline-flex rounded-[8px] border border-zinc-200 bg-zinc-50 p-1"
    >
      {DSR_FRAMEWORKS.map((f) => {
        const active = props.active === f;
        return (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => props.onChange(f)}
            data-framework={f}
            className={[
              "rounded-[6px] px-4 py-1.5 text-sm font-medium transition-colors",
              active
                ? "bg-white text-zinc-900 shadow-[0_1px_2px_0_rgb(24_24_27/0.06)]"
                : "text-zinc-600 hover:text-zinc-900",
            ].join(" ")}
          >
            {f.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Submit card
// ---------------------------------------------------------------------------

function SubmitCard(props: { framework: DsrFramework }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM_FOR(props.framework));
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset region when framework changes (EU↔US-CA) to keep the defaults sane.
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      region: DEFAULT_REGION_FOR[props.framework],
    }));
    setErrors({});
  }, [props.framework]);

  const mutation = useMutation({
    mutationFn: submitDsr,
    onSuccess: (result) => {
      toast.success(
        result.verification_token
          ? "Request submitted. Verification email sent to the subject."
          : "Request submitted.",
      );
      queryClient.invalidateQueries({
        queryKey: complianceDsrQueryKeys.openFor(result.row.framework),
      });
      setForm(EMPTY_FORM_FOR(props.framework));
    },
    onError: (err: Error) => {
      toast.error(err.message || "Unable to submit request.");
    },
  });

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = submitDsrSchema.safeParse({
      email: form.email,
      kind: form.kind,
      region: form.region,
      framework: props.framework,
    });
    if (!parsed.success) {
      setErrors(parsed.errors);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    mutation.mutate(parsed.data);
  }

  return (
    <section
      className={`${CARD} flex flex-col gap-6 p-6`}
      aria-labelledby="submit-heading"
    >
      <div className="flex flex-col gap-1">
        <h2 id="submit-heading" className="text-lg font-semibold text-zinc-900">
          Submit a request
        </h2>
        <p className={HINT}>
          The subject receives a verification email; the request is recorded
          immediately and appears in the open list below.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-6"
        data-submit-endpoint={
          props.framework === "gdpr" ? "/gdpr-dsr/requests" : "/ccpa-dsr/requests"
        }
        noValidate
      >
        <EmailField
          value={form.email}
          onChange={(v) => setForm((p) => ({ ...p, email: v }))}
          error={errors.email}
        />

        <KindField
          value={form.kind}
          onChange={(k) => setForm((p) => ({ ...p, kind: k }))}
          error={errors.kind}
        />

        <RegionField
          framework={props.framework}
          value={form.region}
          onChange={(r) => setForm((p) => ({ ...p, region: r }))}
          error={errors.region}
        />

        <SubmitFooter
          framework={props.framework}
          busy={mutation.isPending}
          onReset={() => {
            setForm(EMPTY_FORM_FOR(props.framework));
            setErrors({});
          }}
        />
      </form>
    </section>
  );
}

function EmailField(props: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor="dsr-email" className={LABEL}>
        Subject email
      </label>
      <input
        id="dsr-email"
        name="email"
        type="email"
        autoComplete="off"
        placeholder="subject@example.com"
        className={INPUT}
        value={props.value}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        aria-invalid={Boolean(props.error)}
        aria-describedby={props.error ? "dsr-email-error" : undefined}
      />
      <p className={HINT}>
        Address on file for the data subject. A verification link is emailed to
        this address before the request is actioned.
      </p>
      {props.error && (
        <p id="dsr-email-error" className={ERROR_TEXT}>
          {props.error}
        </p>
      )}
    </div>
  );
}

function KindField(props: {
  value: DsrKind;
  onChange: (k: DsrKind) => void;
  error?: string;
}) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className={LABEL}>Request kind</legend>
      <div
        role="radiogroup"
        aria-label="Request kind"
        className="flex flex-wrap gap-2"
      >
        {DSR_KINDS.map((k) => {
          const active = props.value === k;
          return (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => props.onChange(k)}
              data-kind={k}
              className={[
                "inline-flex items-center gap-2 rounded-[6px] border px-3 py-1.5 text-sm font-medium transition-colors",
                active
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                  : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300",
              ].join(" ")}
            >
              <span
                aria-hidden="true"
                className={`h-1.5 w-1.5 rounded-full ${
                  active ? "bg-emerald-500" : "bg-zinc-300"
                }`}
              />
              {KIND_LABELS[k]}
            </button>
          );
        })}
      </div>
      {props.error ? (
        <p className={ERROR_TEXT}>{props.error}</p>
      ) : (
        <p className={HINT}>
          Access: copy of stored data. Erasure: permanent deletion. Portability:
          machine-readable export.
        </p>
      )}
    </fieldset>
  );
}

function RegionField(props: {
  framework: DsrFramework;
  value: DsrRegion;
  onChange: (r: DsrRegion) => void;
  error?: string;
}) {
  // CCPA is fixed to US-CA; GDPR defaults to EU but keeps the radio present
  // so operators always see the canonical region in use.
  const locked = props.framework === "ccpa";
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className={LABEL}>Residency region</legend>
      <div
        role="radiogroup"
        aria-label="Residency region"
        className="inline-flex rounded-[6px] border border-zinc-200 bg-zinc-50 p-0.5"
      >
        {REGIONS.map((r) => {
          const active = props.value === r;
          const disabled = locked && r !== "US-CA";
          return (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => props.onChange(r)}
              data-region={r}
              className={[
                "rounded-[6px] px-3 py-1.5 text-sm font-medium transition-colors",
                active
                  ? "bg-white text-zinc-900 shadow-[0_1px_2px_0_rgb(24_24_27/0.06)]"
                  : "text-zinc-600 hover:text-zinc-900",
                disabled ? "cursor-not-allowed opacity-50" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {r}
            </button>
          );
        })}
      </div>
      {props.error ? (
        <p className={ERROR_TEXT}>{props.error}</p>
      ) : (
        <p className={HINT}>
          Request payloads are stored in the region matching the subject's
          residency; the SLA is determined by the active framework.
        </p>
      )}
    </fieldset>
  );
}

function SubmitFooter(props: {
  framework: DsrFramework;
  busy: boolean;
  onReset: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 border-t border-zinc-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
      <p className={HINT}>
        Targets {props.framework === "gdpr"
          ? "POST /api/v1/gdpr-dsr/requests"
          : "POST /api/v1/ccpa-dsr/requests"}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={props.onReset}
          disabled={props.busy}
          className="rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Reset
        </button>
        <button
          type="submit"
          disabled={props.busy}
          className="rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:border-emerald-700 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {props.busy ? "Submitting." : "Submit request"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Open requests table
// ---------------------------------------------------------------------------

function OpenRequestsCard(props: {
  framework: DsrFramework;
  nowFn: () => number;
}) {
  const query = useQuery({
    queryKey: complianceDsrQueryKeys.openFor(props.framework),
    queryFn: () => listOpen(props.framework),
  });

  return (
    <section
      className={`${CARD} flex flex-col gap-4 p-6`}
      aria-labelledby="open-heading"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 id="open-heading" className="text-lg font-semibold text-zinc-900">
            Open requests
          </h2>
          <p className={HINT}>
            Live from {props.framework === "gdpr"
              ? "GET /api/v1/gdpr-dsr/requests"
              : "GET /api/v1/ccpa-dsr/requests"}
            . Terminal rows (fulfilled / rejected / denied) are hidden.
          </p>
        </div>
        <button
          type="button"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {query.isFetching ? "Refreshing." : "Refresh"}
        </button>
      </div>

      {query.isError && (
        <p className={ERROR_TEXT}>
          {(query.error as Error)?.message ?? "Unable to load requests."}
        </p>
      )}

      {query.isSuccess && (query.data?.length ?? 0) === 0 && (
        <p className={HINT}>No open requests. Submit one above.</p>
      )}

      {query.isSuccess && (query.data?.length ?? 0) > 0 && (
        <OpenRequestsTable rows={query.data!} nowMs={props.nowFn()} />
      )}

      {query.isLoading && <p className={HINT}>Loading.</p>}
    </section>
  );
}

function OpenRequestsTable(props: { rows: DsrRow[]; nowMs: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-zinc-200 text-xs font-medium uppercase tracking-wide text-zinc-500">
            <th className="py-2 pr-3">Request ID</th>
            <th className="py-2 pr-3">Email</th>
            <th className="py-2 pr-3">Kind</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Submitted</th>
            <th className="py-2 pr-3">SLA</th>
            <th className="py-2 pr-3 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row) => (
            <OpenRequestsRow key={row.id} row={row} nowMs={props.nowMs} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OpenRequestsRow(props: { row: DsrRow; nowMs: number }) {
  const queryClient = useQueryClient();
  const next = nextStatusesFor(props.row);
  const [nextStatus, setNextStatus] = useState<UnifiedStatus | "">("");

  const mutation = useMutation({
    mutationFn: (args: { status: UnifiedStatus }) =>
      updateStatus({
        framework: props.row.framework,
        id: props.row.id,
        status: args.status,
      }),
    onSuccess: () => {
      toast.success("Status updated.");
      queryClient.invalidateQueries({
        queryKey: complianceDsrQueryKeys.openFor(props.row.framework),
      });
      setNextStatus("");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Unable to update status.");
    },
  });

  const tier = slaTier(props.row.due_at, props.nowMs);
  const countdown = formatSlaCountdown(props.row.due_at, props.nowMs);
  const submittedLabel = useMemo(
    () => new Date(props.row.submitted_at).toISOString().slice(0, 10),
    [props.row.submitted_at],
  );

  return (
    <tr
      className="border-b border-zinc-100 last:border-b-0"
      data-row-id={props.row.id}
    >
      <td className="py-3 pr-3 font-mono text-xs text-zinc-700">
        {props.row.id.slice(0, 12)}
      </td>
      <td className="py-3 pr-3 text-zinc-900">{props.row.email}</td>
      <td className="py-3 pr-3 text-zinc-700 capitalize">
        {props.row.kind}
      </td>
      <td className="py-3 pr-3">
        <StatusPill status={props.row.status} />
      </td>
      <td className="py-3 pr-3 text-zinc-600">{submittedLabel}</td>
      <td className="py-3 pr-3">
        <span
          data-sla-tier={tier}
          data-sla-due={props.row.due_at}
          className={[
            "inline-flex items-center gap-2 rounded-[6px] border px-2 py-0.5 text-xs font-medium",
            SLA_TIER_CLASS[tier],
          ].join(" ")}
        >
          <span
            aria-hidden="true"
            className={[
              "h-1.5 w-1.5 rounded-full",
              tier === "green"
                ? "bg-emerald-500"
                : tier === "amber"
                  ? "bg-amber-500"
                  : "bg-red-500",
            ].join(" ")}
          />
          {countdown}
        </span>
      </td>
      <td className="py-3 pr-3 text-right">
        {next.length === 0 ? (
          <span className={HINT}>—</span>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <select
              aria-label={`Next status for ${props.row.id}`}
              className={`${INPUT} w-40`}
              value={nextStatus}
              onChange={(e) =>
                setNextStatus(e.currentTarget.value as UnifiedStatus)
              }
              disabled={mutation.isPending}
            >
              <option value="">Select.</option>
              {next.map((s) => (
                <option key={s} value={s}>
                  {s.replace("_", " ")}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                if (!nextStatus) {
                  toast.error("Pick a next status first.");
                  return;
                }
                mutation.mutate({ status: nextStatus as UnifiedStatus });
              }}
              disabled={mutation.isPending || !nextStatus}
              className="rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:border-emerald-700 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation.isPending ? "Saving." : "Apply"}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function StatusPill(props: { status: UnifiedStatus }) {
  const colour = (() => {
    switch (props.status) {
      case "pending":
      case "received":
        return "border-zinc-200 bg-zinc-50 text-zinc-700";
      case "in_progress":
      case "verified":
        return "border-sky-200 bg-sky-50 text-sky-700";
      case "fulfilled":
        return "border-emerald-200 bg-emerald-50 text-emerald-700";
      case "rejected":
      case "denied":
        return "border-red-200 bg-red-50 text-red-700";
      default:
        return "border-zinc-200 bg-white text-zinc-700";
    }
  })();
  return (
    <span
      className={[
        "inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium capitalize",
        colour,
      ].join(" ")}
    >
      {props.status.replace("_", " ")}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Doc hint
// ---------------------------------------------------------------------------

function DocHint(props: { framework: DsrFramework }) {
  return (
    <aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
      <p className="font-medium text-zinc-800">Operator checklist</p>
      <ul className="mt-2 flex list-inside list-disc flex-col gap-1">
        <li>
          Verify the subject's identity before transitioning the status beyond
          the initial received / pending state.
        </li>
        <li>
          {props.framework === "gdpr"
            ? "Fulfilment windows are 30 days under GDPR Art. 12(3)."
            : "Fulfilment windows are 45 days under CCPA 1798.130(a)(2)."}
        </li>
        <li>
          Rejections must include a justification — the denial reason is
          persisted to the audit log.
        </li>
      </ul>
    </aside>
  );
}
